import { callFunctionWithResilience, invokeFireAndForget, type ResilienceOptions } from '@/utils/fetchWrapper';

/**
 * Request deduplication map.
 * Key = functionName + JSON.stringify(body)
 * Prevents duplicate in-flight calls from racing components / re-renders.
 */
const activeRequests = new Map<string, Promise<any>>();

/**
 * Smart memory cache with stale-while-revalidate.
 * Key = functionName + JSON.stringify(body)
 */
interface CacheEntry {
  data: any;
  expires: number;    // fresh until
  staleUntil: number; // SWR window — serve stale, revalidate in background
}
const cache = new Map<string, CacheEntry>();

/**
 * TTL configuration per function.
 * Functions not listed here are NOT cached.
 * Payment endpoints are explicitly excluded.
 */
const CACHE_TTL: Record<string, { freshMs: number; staleMs: number }> = {
  'internal-hotelpage':    { freshMs: 30_000, staleMs: 30_000 },
  'liteapi-reviews':       { freshMs: 60_000, staleMs: 60_000 },
  'internal-reviews':      { freshMs: 60_000, staleMs: 60_000 },
  'offline-autocomplete':  { freshMs: 20_000, staleMs: 20_000 },
  'exora-search':          { freshMs: 20_000, staleMs: 20_000 },
};

/** Payment / mutation endpoints — never cached */
const NEVER_CACHE = new Set([
  'stripe-create-payment-intent',
  'stripe-create-checkout',
  'ratehawk-prebook',
  'ratehawk-book',
]);

function getCacheTTL(fnName: string): { freshMs: number; staleMs: number } | null {
  if (NEVER_CACHE.has(fnName) || fnName.startsWith('stripe-')) return null;
  return CACHE_TTL[fnName] ?? null;
}

function dedupeKey(fnName: string, body: any): string {
  try {
    return fnName + '::' + JSON.stringify(body ?? {});
  } catch {
    return '';
  }
}

/**
 * Centralized, resilient API client for all Supabase Edge Function calls.
 *
 * Features:
 * - Smart memory cache with per-function TTL
 * - Stale-while-revalidate (Airbnb technique)
 * - 401 auto-recovery (session refresh + retry)
 * - Configurable timeout (default 15s)
 * - Automatic retries with exponential backoff
 * - Circuit breaker per function
 * - Request deduplication (identical in-flight calls share one Promise)
 * - Null-data guard
 * - Prefetch support for instant navigation
 */
export const apiClient = {
  /**
   * Call an edge function with full resilience + smart caching.
   * Duplicate in-flight calls (same function + body) are deduplicated.
   */
  async invoke<T = any>(
    functionName: string,
    body: any,
    options?: ResilienceOptions
  ): Promise<{ data?: T; error?: Error; userMessage?: string }> {
    const key = dedupeKey(functionName, body);
    const ttl = getCacheTTL(functionName);
    const now = Date.now();

    // ── Cache check ──
    if (key && ttl) {
      const entry = cache.get(key);
      if (entry) {
        if (now < entry.expires) {
          // Fresh cache hit — return immediately
          return { data: entry.data as T };
        }
        if (now < entry.staleUntil) {
          // Stale-while-revalidate: return stale data, refresh in background
          this._revalidateInBackground(functionName, body, key, ttl, options);
          return { data: entry.data as T };
        }
        // Fully expired — remove and fetch fresh
        cache.delete(key);
      }
    }

    // Dedup: return existing in-flight promise if key is non-empty
    if (key && activeRequests.has(key)) {
      return activeRequests.get(key)!;
    }

    const promise = callFunctionWithResilience<T>(functionName, body, options)
      .then((result) => {
        // Cache successful responses only
        if (key && ttl && result.data != null && !result.error) {
          cache.set(key, {
            data: result.data,
            expires: Date.now() + ttl.freshMs,
            staleUntil: Date.now() + ttl.freshMs + ttl.staleMs,
          });
        }
        return result;
      })
      .finally(() => {
        if (key) activeRequests.delete(key);
      });

    if (key) activeRequests.set(key, promise);
    return promise;
  },

  /**
   * Background revalidation for SWR — never throws, never blocks UI.
   */
  _revalidateInBackground(
    functionName: string,
    body: any,
    key: string,
    ttl: { freshMs: number; staleMs: number },
    options?: ResilienceOptions
  ): void {
    // Skip if already revalidating (dedup map covers this)
    if (activeRequests.has(key + '::revalidate')) return;

    const revalPromise = callFunctionWithResilience(functionName, body, options)
      .then((result) => {
        if (result.data != null && !result.error) {
          cache.set(key, {
            data: result.data,
            expires: Date.now() + ttl.freshMs,
            staleUntil: Date.now() + ttl.freshMs + ttl.staleMs,
          });
        }
      })
      .catch(() => {
        // Swallow — stale data remains in cache
      })
      .finally(() => {
        activeRequests.delete(key + '::revalidate');
      });

    activeRequests.set(key + '::revalidate', revalPromise);
  },

  /**
   * Prefetch an edge function into cache without returning data.
   * Uses requestIdleCallback to avoid blocking UI.
   * Safe to call multiple times — dedup prevents duplicate fetches.
   */
  prefetch(functionName: string, body: any, options?: ResilienceOptions): void {
    const key = dedupeKey(functionName, body);
    const ttl = getCacheTTL(functionName);

    // Skip if not cacheable, already cached/fresh, or already in-flight
    if (!key || !ttl) return;
    const entry = cache.get(key);
    if (entry && Date.now() < entry.expires) return;
    if (activeRequests.has(key)) return;

    const doFetch = () => {
      this.invoke(functionName, body, options).catch(() => {
        // Swallow — prefetch is best-effort
      });
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(doFetch, { timeout: 3000 });
    } else {
      setTimeout(doFetch, 100);
    }
  },

  /**
   * Fire-and-forget: for notifications, telemetry, emails.
   * Never throws, never blocks UI.
   */
  fireAndForget(functionName: string, body: any): void {
    invokeFireAndForget(functionName, body);
  },

  /**
   * Clear all cached entries (for testing or manual purge).
   */
  clearCache(): void {
    cache.clear();
  },

  /**
   * Get cache stats for debugging.
   */
  getCacheStats(): { size: number; keys: string[] } {
    return { size: cache.size, keys: Array.from(cache.keys()) };
  },
};
